import type { MouseEvent } from 'react';

import NewWindowIconSm from '@skyscanner-internal/backpack-web/bpk-component-icon/sm/new-window';
import BpkLink from '@skyscanner-internal/backpack-web/bpk-component-link';
import BpkText, {
  TEXT_STYLES,
} from '@skyscanner-internal/backpack-web/bpk-component-text';
import classnames from 'classnames';

import {
  FormattedMessage,
  withI18n,
} from '../../../hotels-utils/services/i18n';
import { isShowHotelName } from '../../../hotels-utils/services/officialWebsite';
import NormalRates from '../NormalRates';

import type {
  I18n,
  Price,
} from '@skyscanner-internal/landing-pages-shared-types/types-shared/hotels-components/types';

import STYLES from './OtherRatesRow.module.scss';

const MAX_NUM_THRESHOLD = 4;

type RateProps = {
  i18n: I18n;
  rate?: Price;
  detailUrl?: string;
  onClick?: Function;
  lessNum?: number;
};

const Rate = ({
  detailUrl = undefined,
  i18n,
  lessNum = undefined,
  onClick = undefined,
  rate = undefined,
}: RateProps) => {
  const showDirectLabel =
    rate && isShowHotelName(rate.isOfficial, rate.partnerId, rate?.partnerType);

  const partneName = (
    <BpkText textStyle={TEXT_STYLES.caption} className={STYLES.RateRow__name}>
      {rate?.name}
    </BpkText>
  );

  const partnerNameWithFeature = (
    <div className={STYLES.RateRow__direct}>
      {partneName}
      {rate?.funnelType === 'dbook' ? null : (
        <NewWindowIconSm className={STYLES.RateRow__newWindowIconSmall} />
      )}
    </div>
  );

  const directLable = (
    <div className={STYLES.RateRow__direct}>
      <BpkText textStyle={TEXT_STYLES.caption}>
        <FormattedMessage label="OTARates_label_direct" />
      </BpkText>
      <NewWindowIconSm className={STYLES.RateRow__newWindowIconSmall} />
    </div>
  );

  const url =
    rate?.funnelType === 'dbook' ? detailUrl : rate?.deepLink || detailUrl;

  return (
    <BpkLink
      className={classnames(STYLES.RateRow, {
        [STYLES[`RateRow--lessNum${lessNum}`]]: lessNum && lessNum <= 3,
      })}
      href={url ?? null}
      blank
      onClick={(e: MouseEvent<HTMLAnchorElement>) => {
        e.stopPropagation();
        if (onClick !== null)
          onClick?.(false, rate, rate?.funnelType === 'dbook', 'other');
      }}
    >
      {showDirectLabel ? directLable : partnerNameWithFeature}
      <BpkText textStyle={TEXT_STYLES.label1}>
        {rate && i18n.formatCurrency(rate.price)}
      </BpkText>
    </BpkLink>
  );
};

type OtherRatesRowProps = {
  i18n: I18n;
  otherRates?: Price[];
  onClick?: Function;
  detailUrl?: string;
};

const OtherRatesRow = ({
  detailUrl = undefined,
  i18n,
  onClick = undefined,
  otherRates = undefined,
}: OtherRatesRowProps) => {
  const displayNum =
    otherRates && otherRates.length > MAX_NUM_THRESHOLD ? 3 : 4;
  const popularRates = otherRates?.slice(0, displayNum);
  const normalRates = otherRates?.slice(displayNum);

  const lessNum = otherRates?.length;

  return (
    <div className={STYLES.OtherRatesRow}>
      {popularRates?.map((rate) => (
        <Rate
          i18n={i18n}
          rate={rate}
          detailUrl={detailUrl}
          onClick={onClick}
          key={rate.partnerId}
          lessNum={lessNum}
        />
      ))}
      {normalRates && normalRates.length > 0 && (
        <NormalRates
          rates={normalRates}
          detailUrl={detailUrl}
          onClick={onClick}
        />
      )}
    </div>
  );
};

export default withI18n(OtherRatesRow);
